import React from "react";
import { Table, InputNumber, Button } from "antd";
import { currencyFormat } from "../utils/price";
import {
  getSelectedProductsColumns,
  getSelectedProductsDataSource,
} from "../utils/table";

/**
 * Компонент для отображения списка выбранных дверей (selectedProducts) 
 * и их аксессуаров (doorKnob, doorPanel, doorstep, latch).
 *
 * @param {Array} selectedProducts        Список товаров (каждый — дверь + потенциальные аксессуары)
 * @param {Function} setSelectedProducts  Функция обновления списка
 * @param {Function} deleteSelectedProduct  Коллбэк для удаления самой «двери»
 * @param {number} increasePrice         Глобальное удорожание (процент)
 * @param {Array} latch                  Справочник поворотников (механизмов) для смены в строке
 * @param {boolean} isEditMode           true, если разрешено редактировать/удалять аксессуары
 */
const SelectedProductsList = ({
  selectedProducts,
  setSelectedProducts,
  deleteSelectedProduct,
  increasePrice = 0,
  latch = [],
  isEditMode = false,
}) => {
  // Основные колонки (для дверей)
  const columns = getSelectedProductsColumns();

  // Формируем «строки» таблицы (двери)
  const dataSource = getSelectedProductsDataSource(
    selectedProducts,
    setSelectedProducts,
    deleteSelectedProduct,
    increasePrice,
    latch
  );

  // Колонки для под-таблицы «аксессуаров»
  const accessoryColumns = [
    {
      title: "Аксессуар",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Цена",
      dataIndex: "basePrice",
      key: "basePrice",
      render: (val) => currencyFormat(val),
    },
    {
      title: "Скидка, %",
      dataIndex: "discount",
      key: "discount",
      render: (discount, accessory) => (
        <InputNumber
          min={0}
          max={100}
          style={{ width: 60 }}
          disabled={!isEditMode}
          value={discount}
          onChange={(val) =>
            updateAccessoryValue(accessory.parentRecord, accessory.key, "discount", val)
          }
        />
      ),
    },
    {
      title: "Удорож., %",
      dataIndex: "appreciation",
      key: "appreciation",
      render: (appreciation, accessory) => (
        <InputNumber
          min={0}
          max={100}
          style={{ width: 60 }}
          disabled={!isEditMode}
          value={appreciation}
          onChange={(val) =>
            updateAccessoryValue(accessory.parentRecord, accessory.key, "appreciation", val)
          }
        />
      ),
    },
    {
      title: "Сумма",
      dataIndex: "finalPrice",
      key: "finalPrice",
      render: (val) => currencyFormat(val),
    },
    {
      title: "Действия",
      key: "actions",
      render: (_, accessory) => (
        <Button
          danger
          disabled={!isEditMode}
          onClick={() => removeAccessory(accessory.parentRecord, accessory.key)}
        >
          Удалить
        </Button>
      ),
    },
  ];

  /**
   * Удаляем выбранный аксессуар: обнуляем соответствующее поле
   * (doorKnob, doorPanel, doorstep, latch), если совпадает accessoryKey
   */
  const removeAccessory = (doorRecord, accessoryKey) => {
    const newList = selectedProducts.map((p) => {
      // Сравниваем по uuid или _id
      const productKey = p.uuid || p._id;
      if (productKey === doorRecord.key) {
        // Создаем новый объект вместо мутации
        const updatedProduct = { ...p };
        let hasChanges = false;
        
        // Проверяем и удаляем каждый аксессуар
        if (
          updatedProduct.doorKnob &&
          (updatedProduct.doorKnob._id === accessoryKey || updatedProduct.doorKnob.name === accessoryKey)
        ) {
          updatedProduct.doorKnob = null;
          hasChanges = true;
        }
        if (
          updatedProduct.doorPanel &&
          (updatedProduct.doorPanel._id === accessoryKey || updatedProduct.doorPanel.name === accessoryKey)
        ) {
          updatedProduct.doorPanel = null;
          hasChanges = true;
        }
        if (
          updatedProduct.doorstep &&
          (updatedProduct.doorstep._id === accessoryKey || updatedProduct.doorstep.name === accessoryKey)
        ) {
          updatedProduct.doorstep = null;
          hasChanges = true;
        }
        if (
          updatedProduct.latch &&
          (updatedProduct.latch._id === accessoryKey || updatedProduct.latch.name === accessoryKey)
        ) {
          updatedProduct.latch = null;
          hasChanges = true;
        }
        
        // Возвращаем обновленный объект только если были изменения
        return hasChanges ? updatedProduct : p;
      }
      return p;
    });
    setSelectedProducts(newList);
  };

  /**
   * Обновляем скидку (accessoryDiscount) или удорожание (accessoryAppreciation)
   * у нужного аксессуара
   */
  const updateAccessoryValue = (doorRecord, accessoryKey, field, newVal) => {
    const newList = selectedProducts.map((p) => {
      const productKey = p.uuid || p._id;
      if (productKey === doorRecord.key) {
        // Создаем новый объект вместо мутации
        const updatedProduct = { ...p };
        let hasChanges = false;
        
        const checkAndUpdate = (acc, fieldName) => {
          if (!acc) return acc;
          const isSame = acc._id === accessoryKey || acc.name === accessoryKey;
          if (isSame) {
            hasChanges = true;
            // Создаем новый объект аксессуара с обновленным полем
            return {
              ...acc,
              [field === "discount" ? "accessoryDiscount" : "accessoryAppreciation"]: newVal,
            };
          }
          return acc;
        };
        
        updatedProduct.doorKnob = checkAndUpdate(updatedProduct.doorKnob, "doorKnob");
        updatedProduct.doorPanel = checkAndUpdate(updatedProduct.doorPanel, "doorPanel");
        updatedProduct.doorstep = checkAndUpdate(updatedProduct.doorstep, "doorstep");
        updatedProduct.latch = checkAndUpdate(updatedProduct.latch, "latch");
        
        return hasChanges ? updatedProduct : p;
      }
      return p;
    });
    setSelectedProducts(newList);
  };

  return (
    <Table
      bordered
      pagination={false}
      columns={columns}
      dataSource={dataSource}
      expandable={{
        expandedRowRender: (record) => {
          if (!record.accessories || record.accessories.length === 0) {
            return <div>Нет аксессуаров</div>;
          }
          // Преобразуем accessories для под-таблицы
          const accessoryData = record.accessories.map((acc) => ({
            key: acc.key || acc.label,
            label: acc.label,
            basePrice: acc.product?.price || 0,
            discount: acc.discount || 0,
            appreciation: acc.appreciation || 0,
            finalPrice: acc.finalPrice || 0,
            parentRecord: record,
          }));

          return (
            <Table
              size="small"
              columns={accessoryColumns}
              dataSource={accessoryData}
              pagination={false}
              style={{ marginLeft: 40 }}
            />
          );
        },
        rowExpandable: (record) =>
          record.accessories && record.accessories.length > 0,
      }}
    />
  );
};

export default SelectedProductsList;
